import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  MilitaryTech as HeadCoachIcon,
  Groups as CoachIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import { TeamMember, User } from '../../types';
import EditDisplayNameDialog from '../common/EditDisplayNameDialog';
import { sortByFirstName } from '../../utils/sortByFirstName';

interface RoleManagementSectionProps {
  teamId: string;
  onRoleUpdated: () => void;
}

const RoleManagementSection: React.FC<RoleManagementSectionProps> = ({
  teamId,
  onRoleUpdated,
}) => {
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [memberUsers, setMemberUsers] = useState<Map<string, User>>(new Map());
  const [headCoachId, setHeadCoachId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canAssignRoles, setCanAssignRoles] = useState(false);
  const [canManageTeam, setCanManageTeam] = useState(false);
  const [editNameMember, setEditNameMember] = useState<{ userId: string; displayName: string } | null>(null);

  // Dialog states
  const [assignCoachDialogOpen, setAssignCoachDialogOpen] = useState(false);
  const [assignAdminDialogOpen, setAssignAdminDialogOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [assigningRole, setAssigningRole] = useState(false);

  useEffect(() => {
    if (teamId && user) {
      loadData();
    }
  }, [teamId, user]);

  const loadData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Check if user can assign roles and manage team (for editing names)
      const [canAssign, canManage] = await Promise.all([
        teamService.canAssignRoles(teamId, user.id),
        teamService.canManageTeam(teamId, user.id),
      ]);
      setCanAssignRoles(canAssign);
      setCanManageTeam(canManage);

      // Get head coach ID
      const hcId = await teamService.getHeadCoachId(teamId);
      setHeadCoachId(hcId);
      
      // Get all team members
      const teamMembers = await teamService.getTeamMembers(teamId);
      const userMap = new Map<string, User>();
      for (const member of teamMembers) {
        const memberUser = await userService.getUserById(member.athleteId);
        if (memberUser) {
          userMap.set(member.athleteId, memberUser);
        }
      }
      setMemberUsers(userMap);
      setMembers(sortByFirstName(teamMembers, m => userMap.get(m.athleteId)?.displayName));
    } catch (err: any) {
      setError(err.message || 'Failed to load role information');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCoach = async () => {
    if (!selectedMemberId || !user) return;
    
    setAssigningRole(true);
    setError('');
    
    try {
      const selectedMember = members.find(m => m.athleteId === selectedMemberId);
      if (!selectedMember) {
        throw new Error('Selected member not found');
      }
      
      await teamService.assignCoachRole(teamId, selectedMember.id, user.id);
      setAssignCoachDialogOpen(false);
      setSelectedMemberId('');
      await loadData();
      onRoleUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to assign coach role');
    } finally {
      setAssigningRole(false);
    }
  };

  const handleRemoveCoach = async (memberId: string) => {
    if (!user) return;
    
    if (!window.confirm('Are you sure you want to remove the coach role from this member?')) {
      return;
    }
    
    setError('');
    
    try {
      await teamService.removeCoachRole(teamId, memberId, user.id);
      await loadData();
      onRoleUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to remove coach role');
    }
  };

  const handleAssignAdmin = async () => {
    if (!selectedMemberId || !user) return;
    
    setAssigningRole(true);
    setError('');
    
    try {
      const selectedMember = members.find(m => m.athleteId === selectedMemberId);
      if (!selectedMember) {
        throw new Error('Selected member not found');
      }
      
      await teamService.assignAdminRole(teamId, selectedMember.id, user.id);
      setAssignAdminDialogOpen(false);
      setSelectedMemberId('');
      await loadData();
      onRoleUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to assign admin role');
    } finally {
      setAssigningRole(false);
    }
  };

  const handleRemoveAdmin = async (memberId: string) => {
    if (!user) return;
    
    if (!window.confirm('Are you sure you want to remove the admin role from this member?')) {
      return;
    }
    
    setError('');
    
    try {
      await teamService.removeAdminRole(teamId, memberId, user.id);
      await loadData();
      onRoleUpdated();
    } catch (err: any) {
      setError(err.message || 'Failed to remove admin role');
    }
  };

  const getMemberDisplayName = (memberId: string): string => {
    const memberUser = memberUsers.get(memberId);
    return memberUser?.displayName || memberId;
  };

  const getRoleBadge = (member: TeamMember): { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' } => {
    if (member.isAdmin) {
      if (member.role === 'headCoach') {
        return { label: 'Head Coach (Admin)', color: 'error' };
      } else if (member.role === 'coach') {
        return { label: 'Coach (Admin)', color: 'warning' };
      } else {
        return { label: 'Athlete (Admin)', color: 'info' };
      }
    } else {
      if (member.role === 'headCoach') {
        return { label: 'Head Coach', color: 'error' };
      } else if (member.role === 'coach') {
        return { label: 'Coach', color: 'primary' };
      } else {
        return { label: 'Athlete', color: 'default' };
      }
    }
  };

  // Filter members by role
  const headCoach = members.find(m => m.role === 'headCoach');
  const coaches = members.filter(m => m.role === 'coach' && m.athleteId !== headCoachId);
  const admins = members.filter(m => m.isAdmin);
  const eligibleForCoach = members.filter(m => 
    m.role !== 'coach' && 
    m.role !== 'headCoach' && 
    m.athleteId !== headCoachId
  );
  const eligibleForAdmin = members.filter(m => !m.isAdmin);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!canAssignRoles && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only head coaches and admins can manage roles.
        </Alert>
      )}

      {/* Head Coach Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeadCoachIcon color="error" />
            Head Coach
          </Typography>
        </Box>
        {headCoach ? (
          <List>
            <ListItem>
              <ListItemText
                primary={getMemberDisplayName(headCoach.athleteId)}
                secondary={memberUsers.get(headCoach.athleteId)?.email}
              />
              <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {canManageTeam && (
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => {
                      const memberUser = memberUsers.get(headCoach.athleteId);
                      if (memberUser) setEditNameMember({ userId: memberUser.id, displayName: memberUser.displayName });
                    }}
                    aria-label="Edit name"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                <Chip
                  label={getRoleBadge(headCoach).label}
                  color={getRoleBadge(headCoach).color}
                  size="small"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No head coach assigned
          </Typography>
        )}
      </Paper>

      {/* Coaches Section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CoachIcon color="primary" />
            Coaches ({coaches.length})
          </Typography>
          {canAssignRoles && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAssignCoachDialogOpen(true)}
            >
              Assign Coach
            </Button>
          )}
        </Box>
        {coaches.length > 0 ? (
          <List>
            {coaches.map((coach) => {
              const badge = getRoleBadge(coach);
              return (
                <ListItem key={coach.id}>
                  <ListItemText
                    primary={getMemberDisplayName(coach.athleteId)}
                    secondary={memberUsers.get(coach.athleteId)?.email}
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {canManageTeam && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          const memberUser = memberUsers.get(coach.athleteId);
                          if (memberUser) setEditNameMember({ userId: memberUser.id, displayName: memberUser.displayName });
                        }}
                        aria-label="Edit name"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Chip label={badge.label} color={badge.color} size="small" />
                    {canAssignRoles && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveCoach(coach.id)}
                        color="error"
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No coaches assigned
          </Typography>
        )}
      </Paper>

      {/* Admins Section */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminIcon color="info" />
            Admins ({admins.length})
          </Typography>
          {canAssignRoles && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAssignAdminDialogOpen(true)}
            >
              Assign Admin
            </Button>
          )}
        </Box>
        {admins.length > 0 ? (
          <List>
            {admins.map((admin) => {
              const badge = getRoleBadge(admin);
              return (
                <ListItem key={admin.id}>
                  <ListItemText
                    primary={getMemberDisplayName(admin.athleteId)}
                    secondary={memberUsers.get(admin.athleteId)?.email}
                  />
                  <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {canManageTeam && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          const memberUser = memberUsers.get(admin.athleteId);
                          if (memberUser) setEditNameMember({ userId: memberUser.id, displayName: memberUser.displayName });
                        }}
                        aria-label="Edit name"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Chip label={badge.label} color={badge.color} size="small" />
                    {canAssignRoles && (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemoveAdmin(admin.id)}
                        color="error"
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No admins assigned
          </Typography>
        )}
      </Paper>

      {/* Assign Coach Dialog */}
      <Dialog open={assignCoachDialogOpen} onClose={() => setAssignCoachDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Coach Role</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Team Member</InputLabel>
            <Select
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              label="Select Team Member"
              disabled={assigningRole}
            >
              {eligibleForCoach.map((member) => {
                const memberUser = memberUsers.get(member.athleteId);
                return (
                  <MenuItem key={member.id} value={member.athleteId}>
                    {memberUser?.displayName || member.athleteId}
                    {member.isAdmin && (
                      <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        (Admin)
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignCoachDialogOpen(false)} disabled={assigningRole}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignCoach}
            variant="contained"
            disabled={assigningRole || !selectedMemberId}
          >
            {assigningRole ? <CircularProgress size={20} /> : 'Assign Coach'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Admin Dialog */}
      <Dialog open={assignAdminDialogOpen} onClose={() => setAssignAdminDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Admin Role</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Admin role can be assigned to any team member (head coach, coach, or athlete).
            Admins have the highest authority and can manage all team settings.
          </Alert>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Team Member</InputLabel>
            <Select
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              label="Select Team Member"
              disabled={assigningRole}
            >
              {eligibleForAdmin.map((member) => {
                const memberUser = memberUsers.get(member.athleteId);
                const badge = getRoleBadge(member);
                return (
                  <MenuItem key={member.id} value={member.athleteId}>
                    {memberUser?.displayName || member.athleteId}
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      ({badge.label})
                    </Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignAdminDialogOpen(false)} disabled={assigningRole}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignAdmin}
            variant="contained"
            disabled={assigningRole || !selectedMemberId}
          >
            {assigningRole ? <CircularProgress size={20} /> : 'Assign Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Display Name Dialog - coaches can edit team member names */}
      {editNameMember && user && (
        <EditDisplayNameDialog
          open={!!editNameMember}
          onClose={() => setEditNameMember(null)}
          currentDisplayName={editNameMember.displayName}
          onSave={async (newDisplayName) => {
            await userService.updateDisplayName(editNameMember.userId, newDisplayName, {
              teamId,
              currentUserId: user.id,
            });
            await loadData();
          }}
          title="Edit Team Member Name"
          label="Display name"
        />
      )}
    </Box>
  );
};

export default RoleManagementSection;

